import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { dateValidator, dateBeforeValidator } from '@liberty-financial/ng-core';
import { diff } from 'deep-object-diff';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ContactSummary } from '@features/contacts/models';
import { ContactType } from '@features/contacts/models/contactType';
import { ContactReferrerArrangement } from '@features/contacts/models/referrer-arrangement';
import { Trust } from '@features/contacts/models/trust';
import { Beneficiary } from '@features/contacts/models/trust/beneficiary';
import { TrustService } from '@features/contacts/services/trust.service';
import { TagEntityType } from '@features/opportunities/services/tag.service';
import { Tag } from '@shared/models/tag';
import {
  commandEvent,
  CommandEvent
} from '@shared/components/load/command/command.component';
import { StandardSchemaUtil } from '@shared/utilities/standard-schema-util';

export interface SchemaDefinition {
  trust: Trust.SchemaPropertyDefinition;
  beneficiary: Beneficiary.SchemaPropertyDefinition;
}

export interface Content {
  trust: Trust.Model;
  contacts: ContactSummary[];
}

export type LoadContent = {
  schema: SchemaDefinition;
  content: Content;
};

@Component({ template: '' })
export abstract class TrustDetailsComponent implements OnInit {
  @Input() cancel: () => void = () => {};
  @Input() saved: () => void = () => {};

  trustId!: string;
  tagEntityType: TagEntityType = TagEntityType.Trusts;
  pendingTags: Tag[] = [];

  private loadSchema$: Observable<SchemaDefinition> = combineLatest([
    this.trustService.getTrustDefinition(),
    this.trustService.getBeneficiaryDefinition()
  ]).pipe(map(([trust, beneficiary]) => ({ trust, beneficiary })));

  public load$!: Observable<LoadContent>;
  public load!: LoadContent;
  protected change$: Subject<Partial<Trust.Model>> = new Subject<
    Partial<Trust.Model>
  >();
  public update$: Observable<CommandEvent<object>> = commandEvent(
    this.change$,
    (x) => this.trustService.updateTrust(this.trustId, x)
  );

  formGroup = this.fb.group({
    name: this.fb.nonNullable.control('', [Validators.maxLength(150)]),
    trustType: this.fb.nonNullable.control<Trust.TrustType>(
      null as unknown as Trust.TrustType
    ),
    businessNumber: [null as string | null],
    foundationDate: [
      null as string | null,
      [dateValidator(), dateBeforeValidator()]
    ],
    jurisdiction: [null as string | null],
    settlorName: [null as string | null, [Validators.maxLength(160)]],
    managerName: [null as string | null, [Validators.maxLength(160)]],
    trustees: [[] as string[]],
    beneficiaries: [[] as Beneficiary.Model[]],
    contactType: [null as ContactType | null],
    status: [null as string | null],
    nmbAffiliateId: [null as string | null],
    referrerArrangement: [null as ContactReferrerArrangement.Model | null]
  });

  constructor(
    protected trustService: TrustService,
    private readonly fb: FormBuilder
  ) {}

  ngOnInit(): void{
    this.load$ = combineLatest([this.loadSchema$, this.content()]).pipe(
      map(([schema, content]) => ({ schema, content })),
      tap((x) => {
        StandardSchemaUtil.attachFormGroupToSchema(
          this.formGroup,
          x.schema.trust
        );
        this.formGroup.patchValue(x.content.trust);
        this.load = x;
      })
    );
  }

  abstract get title(): string;
  abstract get saveLabel(): string;
  abstract readonly isNewEntity: boolean;
  abstract content(): Observable<Content>;

  onTagsChanged(tags: Tag[]): void {
    this.pendingTags = tags;
  }

  save() {
    // this forces the validation message to show when no trustees/beneficiaries have been added.
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      return;
    }
    const formValue = this.formGroup.getRawValue();
    
    // Clear referrerArrangement if contact type is not Referrer
    if (formValue.contactType !== ContactType.Referrer) {
      formValue.referrerArrangement = null;
    }
    
    let delta = diff(
      this.load.content.trust,
      formValue
    ) as Partial<Trust.Model>;

    if (delta.trustees) {
      delta.trustees = formValue.trustees || undefined;
    }

    if (delta.beneficiaries) {
      delta.beneficiaries = formValue.beneficiaries || undefined;
    }

    if (delta.referrerArrangement) {
      delta.referrerArrangement =
        (formValue.referrerArrangement as ContactReferrerArrangement.Model) ||
        null;
    }

    this.change$.next(delta);
  }
}
